export type Language = 'es' | 'en';

export interface LocalizedString {
  es: string;
  en: string;
}

export interface LocalizedArray {
  es: string[];
  en: string[];
}

export function resolveAssetUrl(asset?: string | LocalizedString, lang: Language = 'es'): string | undefined {
  if (!asset) return undefined;
  if (typeof asset === 'string') return asset;
  return asset[lang] || asset.es;
}

export interface ModulePage {
  pageId: string;
  title: LocalizedString;
  content: LocalizedString;
  imageUrl?: string | LocalizedString;
  keyTakeaways?: LocalizedArray;
}

export interface Module {
  moduleId: string;
  title: LocalizedString;
  videoUrl: string;
  durationMinutes: number;
  pages?: ModulePage[];
  moduleAssessment?: Question;
}

export interface MiniGameOption {
  optionId: string;
  text: LocalizedString;
  isCorrect: boolean;
  feedback: LocalizedString;
  points: number;
}

export interface MiniGame {
  gameId: string;
  title: LocalizedString;
  scenario: LocalizedString;
  options: MiniGameOption[];
}

// 🎮 PSYCHOLOGY-DRIVEN GAME MECHANIC 1: Branching Scenarios (The Illusion of Choice)
export interface ScenarioChoice {
  choiceId: string;
  text: LocalizedString;
  consequenceText: LocalizedString;
  riskChange: number; // e.g. -20% (mitigates) or +35% (danger)
  budgetChange: number; // e.g. -$10,000 or +$0
  reputationChange: number; // e.g. +15% or -30%
  nextNodeId?: string; // moves to deeper branch
  isOptimal?: boolean;
}

export interface ScenarioNode {
  nodeId: string;
  stageTitle: LocalizedString;
  situation: LocalizedString;
  characterRole?: string;
  choices: ScenarioChoice[];
}

export interface BranchingScenario {
  scenarioId: string;
  title: LocalizedString;
  initialRole: LocalizedString;
  initialContext: LocalizedString;
  initialRisk: number; // e.g. 50%
  initialBudget: number; // e.g. $50,000
  initialReputation: number; // e.g. 80%
  startNodeId: string;
  nodes: Record<string, ScenarioNode>;
}

// 🎁 PSYCHOLOGY-DRIVEN GAME MECHANIC 2: Variable Reward Schedules (Mystery Loot Box)
export interface MysteryReward {
  id: string;
  type: 'BADGE_SKIN' | 'XP_BOOST' | 'COMPLIANCE_TOOLKIT' | 'HONORARY_TITLE';
  title: LocalizedString;
  description: LocalizedString;
  rarity: 'COMMON' | 'RARE' | 'EPIC' | 'LEGENDARY';
  icon: string;
  value: string;
}

export interface Question {
  qId: string;
  questionText: LocalizedString;
  options: LocalizedArray;
  correctIndex: number;
  explanation?: string;
}

export interface Course {
  courseId: string;
  category: string;
  jurisdiction: string;
  passingScore: number;
  annualVigencyDays: number;
  bannerUrl?: string | LocalizedString;
  badgeUrl?: string | LocalizedString;
  title: LocalizedString;
  description: LocalizedString;
  modules: Module[];
  questionBank: Question[];
  miniGame?: MiniGame;
  branchingScenario?: BranchingScenario;
}

export interface UserSession {
  token: string;
  userId: string;
  email: string;
  role: 'LEARNER' | 'B2B_ADMIN';
  companyId?: string;
  name?: string;
}

export interface BadgeRecord {
  badgeId: string;
  courseId: string;
  courseTitle: string;
  issuedOn: string; // ISO date string
  validUntil: string; // ISO date string
  badgeUrl?: string | LocalizedString;
  score: number;
}

export interface ModuleProgress {
  moduleId: string;
  completed: boolean;
  completedAt?: string;
}

export type CourseStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';

export interface CourseProgress {
  courseId: string;
  moduleProgress: ModuleProgress[];
  completedPages?: Record<string, string[]>; // moduleId -> pageIds
  miniGameCompleted?: boolean;
  branchingScenarioCompleted?: boolean;
  unlockedMysteryReward?: MysteryReward;
  examPassed: boolean;
  examScore?: number;
  lastVisitedModuleIndex?: number;
  lastVisitedPageIndex?: number;
  progressPercent: number; // 0 to 100
  status: CourseStatus;
  lastActivity?: string;
}

export interface UserProgress {
  userId: string;
  courses: CourseProgress[];
}

export interface CompanyMetrics {
  companyName: string;
  totalEmployees: number;
  activeLicenses: number;
  overallCompliancePercentage: number;
  enrollmentBreakdown: {
    notStarted: number;
    inProgress: number;
    completed: number;
  };
  courseMetrics: Array<{
    courseId: string;
    totalEnrolled: number;
    completedCount: number;
    compliancePercent: number;
  }>;
}

export interface VerificationData {
  valid: boolean;
  status: 'VALID' | 'EXPIRED' | 'NOT_FOUND' | 'REVOKED';
  badgeId?: string;
  recipientEmail?: string;
  courseTitle?: string;
  issuedOn?: string;
  validUntil?: string;
  vigencyRemainingDays?: number;
  openBadgeCredential?: any;
  message: string;
}
